import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";

import swal from "sweetalert2";
import { MatrizRiesgoService } from "../matriz-riesgo.service";
import { FechaBitacoraPipe } from "../fecha-bitacora.pipe";
import {
  Comparacion,
  ComparacionGrupo,
  ComparacionRenglon,
  MatrizPendiente,
} from "src/app/shared/model/iso42001/matriz-riesgo.model";
import { AlertaMonitoreoService } from "../alerta-monitoreo/alerta-monitoreo.service";

/** Una matriz del filtro. */
interface OpcionMatriz {
  idMatriz: number;
  nombre: string;
}

/**
 * «CAMBIOS POR APROBAR»: el control de cambios de las matrices de riesgos.
 *
 * Con iso42001.matriz.aprobacion=true (el valor de producción) lo que se
 * guarda en una matriz NO se aplica: queda como propuesta en
 * iso42001_matriz_pendiente. Hasta el 24/09/2026 no había pantalla para
 * resolverlas y se quedaban atoradas. Aquí se ven, se revisan (hoy contra
 * lo propuesto, campo por campo) y se ACEPTAN —entonces el servidor las
 * aplica por el mismo camino que un guardado normal, con su bitácora— o se
 * DESCARTAN con un motivo obligatorio.
 *
 * UNA SOLA BANDEJA PARA TODAS LAS MATRICES: la tabla de propuestas no
 * guarda la matriz (el servidor la deduce), y el contador del Dashboard
 * cuenta todas. El libro de cada matriz entra con ?idMatriz=N para
 * filtrar; ?id=N abre una propuesta.
 *
 * QUIÉN: aceptar, descartar y ver el detalle son del perfil «Edicion Matriz
 * de Riesgos IA», y el servidor lo exige en cada llamada. La bandeja
 * también: sin el perfil el servidor sólo manda lo que cuentan el tablero
 * y el libro, y aquí sólo se ve el aviso de «Sólo lectura». No hay regla de «cuatro
 * ojos»: quien propuso puede aceptar lo suyo (no existe en el módulo y no
 * se inventó aquí).
 *
 * Listado sobre table-page (src/styles.css); el detalle sobre la base de
 * admin/ajustes (.ajustes-page). Los colores propios van con los tokens
 * de tema y su respaldo clásico: var(--cos-*, literal).
 */
@Component({
  selector: "app-cambios-por-aprobar-ia",
  standalone: true,
  templateUrl: "./cambios-por-aprobar-ia.component.html",
  styleUrls: ["./cambios-por-aprobar-ia.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FechaBitacoraPipe,
  ],
})
export class CambiosPorAprobarIAComponent implements OnInit, OnDestroy {
  /** Cuántas resueltas se traen para «Ya resueltas». */
  private static readonly LIMITE_RESUELTAS = 50;

  pendientes: MatrizPendiente[] = [];
  resueltos: MatrizPendiente[] = [];
  aprobacionActiva = false;
  puedeEditar = false;
  cargando = false;
  errorCarga = "";

  /* ---------- Filtros ---------- */
  vista: "PENDIENTES" | "RESUELTAS" = "PENDIENTES";
  filtroMatriz: number | null = null;
  busca = "";
  matrices: OpcionMatriz[] = [];
  /** La lista ya filtrada: se arma al cambiar algo, no en cada ciclo. */
  filas: MatrizPendiente[] = [];

  /** De dónde se llegó: el botón «Volver a la matriz». */
  idMatrizOrigen: number | null = null;
  /** ?id=N: la propuesta que hay que abrir en cuanto llegue la lista. */
  private idPorAbrir: number | null = null;

  /* ---------- Detalle ---------- */
  abierta: MatrizPendiente | null = null;
  comparacion: Comparacion | null = null;
  comparando = false;
  errorDetalle = "";
  soloCambios = true;
  vistaGrupos: { grupo: ComparacionGrupo; renglones: ComparacionRenglon[] }[] = [];
  ocultos = 0;

  /* ---------- Decisión ---------- */
  descartando = false;
  motivo = "";
  enviando = false;
  readonly maxMotivo = 500;

  private subs: Subscription[] = [];
  private subDetalle: Subscription | null = null;

  private readonly alertaMonitoreo = inject(AlertaMonitoreoService);

  constructor(
    private service: MatrizRiesgoService,
    private ruta: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.ruta.queryParamMap.subscribe((q) => {
        const m = Number(q.get("idMatriz"));
        this.idMatrizOrigen = m > 0 ? m : null;
        this.filtroMatriz = this.idMatrizOrigen;
        const id = Number(q.get("id"));
        this.idPorAbrir = id > 0 ? id : null;
        this.cargar();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.cortaDetalle();
  }

  /* ===================== LISTA ===================== */

  cargar(): void {
    this.cargando = true;
    this.errorCarga = "";
    this.cdr.detectChanges();
    this.service.pendientes(CambiosPorAprobarIAComponent.LIMITE_RESUELTAS).subscribe({
      next: (res) => {
        this.cargando = false;
        // Cuerpo vacío también es falla: el servidor responde 200 aunque truene.
        if (!res || res.codigoError) {
          this.errorCarga = (res && res.mensajeError)
            || "No se pudieron consultar los cambios por aprobar. Inténtalo de nuevo.";
          this.pendientes = [];
          this.resueltos = [];
          this.armaFilas();
          this.cdr.detectChanges();
          return;
        }
        this.pendientes = res.pendientes || [];
        this.resueltos = res.resueltos || [];
        this.aprobacionActiva = !!res.aprobacionActiva;
        this.puedeEditar = !!res.puedeEditar;
        this.armaMatrices();
        this.armaFilas();
        // Si la que está abierta ya no espera (la resolvió alguien más y
        // aceptar o descartar lo acaba de decir), el detalle se cierra: no
        // se queda con el sello «Pendiente» y los botones vivos.
        const abierta = this.abierta;
        if (abierta && abierta.estatus === "PENDIENTE"
            && !this.pendientes.some((x) => x.idPendiente === abierta.idPendiente)) {
          this.volverALista();
        }
        this.cdr.detectChanges();
        this.abrePendienteDeUrl();
      },
      error: () => {
        this.cargando = false;
        this.errorCarga = "No se pudieron consultar los cambios por aprobar. Revisa tu conexión e inténtalo de nuevo.";
        this.cdr.detectChanges();
      },
    });
  }

  /** Las matrices que aparecen en la bandeja, para el filtro. */
  private armaMatrices(): void {
    const vistas = new Map<number, string>();
    [...this.pendientes, ...this.resueltos].forEach((p) => {
      if (p.idMatriz) {
        vistas.set(p.idMatriz, p.matrizNombre || "Matriz " + p.idMatriz);
      }
    });
    this.matrices = Array.from(vistas.entries())
      .map(([idMatriz, nombre]) => ({ idMatriz, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }

  armaFilas(): void {
    const base = this.vista === "PENDIENTES" ? this.pendientes : this.resueltos;
    const q = this.normaliza(this.busca);
    this.filas = base.filter((p) => {
      // Las de matriz desconocida pasan con cualquier filtro: su registro ya
      // no existe (una eliminación aplicada, o un corte cuyo activo se
      // borró mientras esperaba) y el servidor no puede decir de cuál era.
      // Ocultarlas escondía justo las que hay que descartar.
      if (this.filtroMatriz && p.idMatriz && p.idMatriz !== this.filtroMatriz) {
        return false;
      }
      if (!q) {
        return true;
      }
      const texto = [
        this.titulo(p),
        p.folioRegistro,
        p.nombreRegistro,
        p.referencia,
        p.resumen,
        p.solicitudFolio,
        p.proponeNombre,
        p.resuelveNombre,
        p.matrizNombre,
      ].join(" ");
      return this.normaliza(texto).indexOf(q) >= 0;
    });
  }

  cambiaVista(v: "PENDIENTES" | "RESUELTAS"): void {
    this.vista = v;
    this.armaFilas();
    this.cdr.detectChanges();
  }

  limpiarFiltros(): void {
    this.busca = "";
    this.filtroMatriz = null;
    this.armaFilas();
    this.cdr.detectChanges();
  }

  get hayFiltro(): boolean {
    return !!this.busca.trim() || !!this.filtroMatriz;
  }

  /** Nombre de la matriz del filtro, para los textos. */
  get nombreMatrizFiltro(): string {
    const m = this.matrices.find((x) => x.idMatriz === this.filtroMatriz);
    return m ? m.nombre : "";
  }

  /* ===================== TEXTOS ===================== */

  /**
   * Qué se propone, en español: «Alta desde SIA-0044», «Cambio de activo»,
   * «Reactivación de activo»... La BAJA de un activo cubre también la
   * reactivación: lo decide el estatus propuesto.
   */
  titulo(p: MatrizPendiente): string {
    const op = p.operacion;
    switch (p.entidad) {
      case "ACTIVO":
        if (op === "ALTA") {
          return p.solicitudFolio ? "Alta desde " + p.solicitudFolio : "Alta de activo";
        }
        if (op === "MODIFICACION") {
          return "Cambio de activo";
        }
        if (op === "BAJA") {
          return this.esReactivacion(p) ? "Reactivación de activo" : "Baja de activo";
        }
        return "Eliminación de activo";
      case "RIESGO":
        if (op === "ALTA") {
          return "Alta de riesgo";
        }
        return op === "MODIFICACION" ? "Cambio de riesgo" : "Eliminación de riesgo";
      case "CORTE_ACTIVO":
        if (op === "ALTA") {
          return "Corte nuevo de un activo";
        }
        return op === "MODIFICACION" ? "Corrección de un corte de activo" : "Quitar un corte de activo";
      case "CORTE_RIESGO":
        if (op === "ALTA") {
          return "Corte nuevo de un riesgo";
        }
        return op === "MODIFICACION" ? "Corrección de un corte de riesgo" : "Quitar un corte de riesgo";
      default:
        return (p.entidad || "") + " / " + (op || "");
    }
  }

  /** La palabra de la pastilla: Alta, Cambio, Baja, Reactivación, Eliminación. */
  operacionTexto(p: MatrizPendiente): string {
    switch (p.operacion) {
      case "ALTA":
        return p.entidad === "CORTE_ACTIVO" || p.entidad === "CORTE_RIESGO" ? "Corte nuevo" : "Alta";
      case "MODIFICACION":
        return p.entidad === "CORTE_ACTIVO" || p.entidad === "CORTE_RIESGO" ? "Corrección" : "Cambio";
      case "BAJA":
        return this.esReactivacion(p) ? "Reactivación" : "Baja";
      case "ELIMINACION":
        return p.entidad === "CORTE_ACTIVO" || p.entidad === "CORTE_RIESGO" ? "Quitar" : "Eliminación";
      default:
        return p.operacion || "";
    }
  }

  /** El color de la operación: verde lo que entra, rojo lo que sale, azul lo que cambia. */
  tono(p: MatrizPendiente): "alta" | "baja" | "cambio" {
    if (p.operacion === "ALTA" || (p.operacion === "BAJA" && this.esReactivacion(p))) {
      return "alta";
    }
    if (p.operacion === "BAJA" || p.operacion === "ELIMINACION") {
      return "baja";
    }
    return "cambio";
  }

  claseOperacion(p: MatrizPendiente): string {
    return "cpa-op cpa-op--" + this.tono(p);
  }

  /** De qué es: activo, riesgo de proceso, corte... */
  entidadTexto(p: MatrizPendiente): string {
    switch (p.entidad) {
      case "ACTIVO":
        return "Activo";
      case "RIESGO":
        return "Riesgo de proceso";
      case "CORTE_ACTIVO":
        return "Corte de monitoreo de un activo";
      case "CORTE_RIESGO":
        return "Corte de monitoreo de un riesgo";
      default:
        return p.entidad || "";
    }
  }

  private esReactivacion(p: MatrizPendiente): boolean {
    return p.operacion === "BAJA" && (p.estatusPropuesto || "").toUpperCase() === "ACTIVO";
  }

  private esAlta(p: MatrizPendiente): boolean {
    return p.operacion === "ALTA" && (p.entidad === "ACTIVO" || p.entidad === "RIESGO");
  }

  /** ¿La propuesta pendiente es un alta que todavía no tiene folio? */
  esNuevo(p: MatrizPendiente): boolean {
    return this.esAlta(p) && !p.folioRegistro;
  }

  /**
   * El nombre de lo que se da de alta. La referencia es el nombre del
   * activo; en un riesgo sale de la carga (sólo llega al perfil de edición).
   */
  nombreDeAlta(p: MatrizPendiente): string {
    const carga = this.leeCarga(p);
    if (carga) {
      const n = carga["nombreActivo"] || carga["descripcion"];
      if (typeof n === "string" && n.trim()) {
        return this.primeraLinea(n, 90);
      }
    }
    return p.referencia || (p.entidad === "RIESGO" ? "Riesgo nuevo" : "Activo nuevo");
  }

  /** El registro al que toca, con su folio: «ACT-001 · ChatGPT». */
  registroTexto(p: MatrizPendiente): string {
    if (this.esNuevo(p)) {
      return this.nombreDeAlta(p);
    }
    const folio = p.folioRegistro || "";
    const nombre = p.nombreRegistro ? this.primeraLinea(p.nombreRegistro, 70) : "";
    if (folio || nombre) {
      return folio && nombre ? folio + " · " + nombre : folio || nombre;
    }
    return p.referencia || "Ya no está en la matriz";
  }

  estatusTexto(p: MatrizPendiente): string {
    switch (p.estatus) {
      case "APLICADO":
        return "Aplicada";
      case "DESCARTADO":
        return "Descartada";
      default:
        return "Pendiente";
    }
  }

  claseEstatus(p: MatrizPendiente): string {
    // Las mismas pastillas del detalle (.cpa-est). Las globales de
    // table-page__status-badge quedan bajo el mínimo de tinta en clásico.
    switch (p.estatus) {
      case "APLICADO":
        return "cpa-est cpa-est--apl";
      case "DESCARTADO":
        return "cpa-est cpa-est--desc";
      default:
        return "cpa-est cpa-est--pend";
    }
  }

  /** «nació ACT-006» en un alta ya aplicada. */
  nacio(p: MatrizPendiente): string {
    return p.estatus === "APLICADO" && this.esAlta(p) && p.folioRegistro ? p.folioRegistro : "";
  }

  /** Un nombre vacío llega como '' del CONCAT_WS: se ve como «—». */
  nombre(n?: string): string {
    return n && n.trim() ? n.trim() : "—";
  }

  /* ===================== DETALLE ===================== */

  abrir(p: MatrizPendiente): void {
    this.abierta = p;
    this.comparacion = null;
    this.vistaGrupos = [];
    this.ocultos = 0;
    this.errorDetalle = "";
    this.soloCambios = true;
    this.descartando = false;
    this.motivo = "";
    this.cdr.detectChanges();
    window.scrollTo({ top: 0 });
    if (p.estatus === "PENDIENTE" && this.puedeEditar && p.idPendiente) {
      this.cargaDetalle(p.idPendiente);
    }
  }

  volverALista(): void {
    this.cortaDetalle();
    this.abierta = null;
    this.comparacion = null;
    this.vistaGrupos = [];
    this.descartando = false;
    this.motivo = "";
    this.cdr.detectChanges();
  }

  private cargaDetalle(id: number): void {
    this.comparando = true;
    this.cdr.detectChanges();
    this.cortaDetalle();
    this.subDetalle = this.service.detallePendiente(id).subscribe({
      next: (res) => {
        // Si mientras contestaba se abrió otra, esta respuesta ya no va.
        if (!this.abierta || this.abierta.idPendiente !== id) {
          return;
        }
        this.comparando = false;
        if (!res || res.codigoError || !res.comparacion) {
          this.errorDetalle = (res && res.mensajeError)
            || "No se pudo armar la comparación de este cambio.";
          this.cdr.detectChanges();
          return;
        }
        this.comparacion = res.comparacion;
        if (res.pendiente) {
          this.abierta = res.pendiente;
        }
        this.armaVista();
        this.cdr.detectChanges();
      },
      error: () => {
        if (!this.abierta || this.abierta.idPendiente !== id) {
          return;
        }
        this.comparando = false;
        this.errorDetalle = "No se pudo leer el detalle de este cambio. Revisa tu conexión e inténtalo de nuevo.";
        this.cdr.detectChanges();
      },
    });
  }

  private cortaDetalle(): void {
    if (this.subDetalle) {
      this.subDetalle.unsubscribe();
      this.subDetalle = null;
    }
  }

  alternarSoloCambios(): void {
    this.soloCambios = !this.soloCambios;
    this.armaVista();
    this.cdr.detectChanges();
  }

  /**
   * La vista filtrada de la comparación. Por omisión sólo lo que cambia; un
   * bloque que nace o se va se enseña entero, porque su contenido ES el
   * cambio.
   */
  private armaVista(): void {
    const grupos = (this.comparacion && this.comparacion.grupos) || [];
    this.vistaGrupos = [];
    grupos.forEach((g) => {
      if (this.soloCambios && g.estado === "IGUAL") {
        return;
      }
      const completo = !this.soloCambios || g.estado === "AGREGADO" || g.estado === "ELIMINADO";
      const renglones = (g.renglones || []).filter((r) => {
        if (completo) {
          // Entero, pero sin los renglones vacíos de los dos lados.
          return !!(r.antes || r.despues);
        }
        return r.estado !== "IGUAL" && r.estado !== "SIN_DATO";
      });
      this.vistaGrupos.push({ grupo: g, renglones });
    });
    this.ocultos = grupos.length - this.vistaGrupos.length;
  }

  etiquetaEstado(estado?: string): string {
    switch (estado) {
      case "AGREGADO":
        return "Se agrega";
      case "ELIMINADO":
        return "Se quita";
      case "CAMBIADO":
        return "Cambia";
      case "SIN_DATO":
        return "Sin dato";
      default:
        return "Igual";
    }
  }

  claseEstado(estado?: string): string {
    switch (estado) {
      case "AGREGADO":
        return "cpa-marca cpa-marca--alta";
      case "ELIMINADO":
        return "cpa-marca cpa-marca--baja";
      case "CAMBIADO":
        return "cpa-marca cpa-marca--cambio";
      default:
        return "cpa-marca cpa-marca--igual";
    }
  }

  claseFila(estado?: string): string {
    switch (estado) {
      case "AGREGADO":
        return "cpa-fila cpa-fila--alta";
      case "ELIMINADO":
        return "cpa-fila cpa-fila--baja";
      case "CAMBIADO":
        return "cpa-fila cpa-fila--cambio";
      default:
        return "cpa-fila";
    }
  }

  /** Qué pasa al aceptar, dicho antes de apretar el botón. */
  get queAlAceptar(): string {
    const p = this.abierta;
    if (!p) {
      return "";
    }
    if (p.entidad === "ACTIVO" && p.operacion === "ALTA") {
      return p.solicitudFolio
        ? "Nace el activo con su folio ACT, ligado a su ficha del inventario, y la solicitud "
          + p.solicitudFolio + " pasa a decir que está en la Matriz de Riesgos IA con ese folio."
        : "Nace el activo con el siguiente folio ACT de la matriz.";
    }
    if (p.entidad === "RIESGO" && p.operacion === "ALTA") {
      return "Nace el riesgo con el siguiente folio RSK de la matriz.";
    }
    if (p.operacion === "ELIMINACION" && (p.entidad === "ACTIVO" || p.entidad === "RIESGO")) {
      return "Se elimina de la matriz. Antes se guarda una copia: se puede resucitar desde «Eliminados».";
    }
    if (p.operacion === "ALTA") {
      // Un corte nuevo no pisa nada: no deja copia.
      return "Se agrega el corte tal como está arriba.";
    }
    return "Se aplica en la matriz tal como está arriba. Antes se guarda una copia de cómo estaba.";
  }

  /** ¿Es la eliminación de un activo? Su bitácora se borra con él. */
  private eliminaActivo(p: MatrizPendiente): boolean {
    return p.entidad === "ACTIVO" && p.operacion === "ELIMINACION";
  }

  /** La nota de la bitácora en la tarjeta «Decisión». */
  get notaBitacora(): string {
    const p = this.abierta;
    if (p && this.eliminaActivo(p)) {
      return "La bitácora del activo se va con él; queda completa en su copia de «Eliminados».";
    }
    return "La bitácora de la matriz queda a nombre de quien la acepta.";
  }

  /* ===================== DECISIÓN ===================== */

  aceptar(): void {
    const p = this.abierta;
    if (!p || !p.idPendiente || this.enviando) {
      return;
    }
    swal
      .fire({
        title: "¿Aceptar y aplicar?",
        text: this.titulo(p) + (this.esNuevo(p) ? "" : " · " + this.registroTexto(p))
          + (this.eliminaActivo(p)
            ? ". Se elimina de la matriz ahora mismo, con su bitácora; su copia queda en «Eliminados»."
            : ". Se aplica en la matriz ahora mismo y la bitácora queda a tu nombre."),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Aceptar y aplicar",
        cancelButtonText: "Cancelar",
      })
      .then((r) => {
        if (!r.isConfirmed) {
          return;
        }
        this.enviando = true;
        this.cdr.detectChanges();
        this.service.aceptarPendiente(p.idPendiente!).subscribe({
          next: (res) => {
            this.enviando = false;
            if (!res || res.codigoError) {
              this.cdr.detectChanges();
              swal.fire("No se aplicó", (res && res.mensajeError) || "Error desconocido.", "warning")
                .then(() => this.cargar());
              return;
            }
            const hecho = res.pendiente || p;
            this.volverALista();
            swal.fire("Cambio aplicado", this.textoAplicado(hecho, p), "success");
            this.cargar();
            // Si era un corte, la alerta flotante se quita sin esperar.
            this.alertaMonitoreo.refrescar();
          },
          error: () => {
            this.enviando = false;
            this.cdr.detectChanges();
            swal.fire("Error", "No se pudo aceptar el cambio. No se aplicó nada.", "error");
          },
        });
      });
  }

  /**
   * El aviso de éxito, según lo que se aplicó. `p` es la propuesta releída
   * después de aplicarla; `antes`, la que estaba abierta: en una eliminación
   * el registro ya no existe y su nombre sólo queda en ésa.
   */
  private textoAplicado(p: MatrizPendiente, antes: MatrizPendiente): string {
    const folio = this.nacio(p);
    if (folio) {
      return p.solicitudFolio
        ? "Nació " + folio + ". La solicitud " + p.solicitudFolio
          + " ya dice que está en la Matriz de Riesgos IA como " + folio + "."
        : "Nació " + folio + " en la matriz.";
    }
    const cual = this.registroTexto(antes);
    if (p.operacion === "ELIMINACION") {
      if (p.entidad === "ACTIVO" || p.entidad === "RIESGO") {
        return "Se eliminó " + cual + " de la matriz. Su copia quedó en «Eliminados».";
      }
      return "Se quitó el corte de " + cual + ", con su renglón en la bitácora.";
    }
    if (p.operacion === "BAJA") {
      return cual + (this.esReactivacion(p) ? " quedó reactivado" : " quedó dado de baja")
        + ", con su renglón en la bitácora.";
    }
    return "Ya está en la matriz, con su renglón en la bitácora.";
  }

  empezarDescarte(): void {
    this.descartando = true;
    this.motivo = "";
    this.cdr.detectChanges();
    setTimeout(() => {
      const el = document.getElementById("cpa-motivo");
      if (el) {
        el.focus();
      }
    });
  }

  cancelarDescarte(): void {
    this.descartando = false;
    this.motivo = "";
    this.cdr.detectChanges();
  }

  descartar(): void {
    const p = this.abierta;
    const motivo = (this.motivo || "").trim();
    if (!p || !p.idPendiente || this.enviando) {
      return;
    }
    if (!motivo) {
      swal.fire("Falta el motivo", "Escribe por qué se descarta: quien lo propuso tiene que poder leerlo.", "warning");
      return;
    }
    this.enviando = true;
    this.cdr.detectChanges();
    this.service.descartarPendiente(p.idPendiente, motivo).subscribe({
      next: (res) => {
        this.enviando = false;
        if (!res || res.codigoError) {
          this.cdr.detectChanges();
          swal.fire("No se descartó", (res && res.mensajeError) || "Error desconocido.", "warning")
            .then(() => this.cargar());
          return;
        }
        this.volverALista();
        swal.fire(
          "Propuesta descartada",
          "La matriz no cambió. El motivo queda a la vista en «Ya resueltas».",
          "success"
        );
        this.cargar();
      },
      error: () => {
        this.enviando = false;
        this.cdr.detectChanges();
        swal.fire("Error", "No se pudo descartar el cambio.", "error");
      },
    });
  }

  /* ===================== NAVEGACIÓN ===================== */

  /** De vuelta al libro de la matriz de la que se llegó, o al tablero. */
  volverAMatriz(): void {
    const id = this.idMatrizOrigen;
    if (id === 1) {
      this.router.navigate(["/RIESGOS/matrizRiesgosIA"]);
    } else if (id) {
      this.router.navigate(["/RIESGOS/matriz", id]);
    } else {
      this.router.navigate(["/RIESGOS/gestionRiesgos"]);
    }
  }

  private abrePendienteDeUrl(): void {
    // Sin el perfil el servidor sólo manda lo que usan los contadores: no
    // hay nada que abrir.
    if (!this.idPorAbrir || !this.puedeEditar) {
      return;
    }
    const id = this.idPorAbrir;
    this.idPorAbrir = null;
    const p = [...this.pendientes, ...this.resueltos].find((x) => x.idPendiente === id);
    if (p) {
      if (p.estatus !== "PENDIENTE") {
        this.vista = "RESUELTAS";
        this.armaFilas();
      }
      this.abrir(p);
    }
  }

  /* ===================== APOYO ===================== */

  private leeCarga(p: MatrizPendiente): { [k: string]: unknown } | null {
    if (!p.carga) {
      return null;
    }
    try {
      const o = JSON.parse(p.carga);
      return o && typeof o === "object" && !Array.isArray(o) ? o : null;
    } catch {
      return null;
    }
  }

  private primeraLinea(s: string, max: number): string {
    const t = (s || "").split("\n")[0].trim();
    return t.length <= max ? t : t.substring(0, max - 1) + "…";
  }

  private normaliza(s: string): string {
    return (s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .trim();
  }

  trackPendiente(_: number, p: MatrizPendiente): number | undefined {
    return p.idPendiente;
  }
}
